/**
 * Email Management Component
 * Main email interface with modern design and attachment support
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Grid,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Fab,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Divider,
  Badge,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Search as SearchIcon,
  Email as EmailIcon,
  Add as AddIcon,
  Inbox as InboxIcon,
  Send as SendIcon,
  Drafts as DraftIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
  Star as StarIcon,
  Folder as FolderIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Business as BusinessIcon,
  Close as CloseIcon
} from '@mui/icons-material';

import { emailService, EmailData, EmailFolder, EmailLabel } from '../../services/emailService';
import { useNotification } from '../../contexts/NotificationContext';
import EmailList from './EmailList';
import EmailDetail from './EmailDetail';
import EmailCompose from './EmailCompose';
import EmailFilters from './EmailFilters';
import SupplierManagementDialog from '../cases/supplier/SupplierManagementDialog';

// Styled components following the design framework
const StyledContainer = styled(Container)(({ theme }) => ({
  maxWidth: '1400px',
  margin: '0 auto',
  padding: theme.spacing(3),
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[3],
  height: '100%'
}));

const SidebarPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[2],
  height: 'calc(100vh - 200px)',
  overflow: 'auto'
}));

const SearchTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius * 2,
    '&:hover fieldset': {
      borderColor: theme.palette.primary.light
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2
    }
  }
}));

const StyledFab = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  zIndex: theme.zIndex.speedDial
}));

const FolderListItem = styled(ListItemButton)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(0.5),
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.light + '20',
    '&:hover': {
      backgroundColor: theme.palette.primary.light + '30'
    }
  }
}));

const EmailManagement: React.FC = () => {
  // State management
  const [emails, setEmails] = useState<EmailData[]>([]);
  const [folders, setFolders] = useState<{system: EmailFolder[], custom: EmailFolder[]}>({
    system: [],
    custom: []
  });
  const [labels, setLabels] = useState<EmailLabel[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<EmailData | null>(null);
  const [selectedFolder, setSelectedFolder] = useState<string>('inbox');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [composeOpen, setComposeOpen] = useState<boolean>(false);
  const [composeMode, setComposeMode] = useState<'compose' | 'reply' | 'forward'>('compose');
  const [replyToEmail, setReplyToEmail] = useState<EmailData | null>(null);
  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);
  const [supplierDialogOpen, setSupplierDialogOpen] = useState<boolean>(false);
  const [filters, setFilters] = useState<any>({});

  const { showNotification } = useNotification();

  // Load initial data
  useEffect(() => {
    loadFolders();
    loadLabels();
    loadEmails();
  }, []);

  // Load emails when folder or filters change
  useEffect(() => {
    loadEmails();
  }, [selectedFolder, filters]);

  const loadFolders = async () => {
    try {
      const foldersData = await emailService.getFolders();
      setFolders(foldersData);
    } catch (error) {
      showNotification('Error loading folders', 'error');
    }
  };

  const loadLabels = async () => {
    try {
      const labelsData = await emailService.getLabels();
      setLabels(labelsData);
    } catch (error) {
      showNotification('Error loading labels', 'error');
    }
  };

  const loadEmails = async () => {
    setLoading(true);
    try {
      const emailFilters = {
        folder: selectedFolder,
        search: searchQuery || undefined,
        ...filters
      };
      const emailsData = await emailService.getEmails(emailFilters);
      setEmails(Array.isArray(emailsData) ? emailsData : []);
    } catch (error) {
      showNotification('Error loading emails', 'error');
      console.error('Error loading emails:', error);
      setEmails([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadEmails();
  };

  const handleEmailSelect = (email: EmailData) => {
    setSelectedEmail(email);
    if (email.status === 'unread') {
      handleEmailUpdate(email.id, { status: 'read' });
    }
  };

  const handleEmailUpdate = async (emailId: string, updates: Partial<EmailData>) => {
    console.group('📧 EmailManagement - handleEmailUpdate');
    console.log('📝 Email ID:', emailId);
    console.log('🎯 Updates:', updates);
    
    try {
      console.log('🌐 Calling emailService.updateEmail...');
      await emailService.updateEmail(emailId, updates);
      console.log('✅ Email updated successfully in database');
      
      console.log('🔄 Reloading emails...');
      loadEmails();
      
      if (selectedEmail && selectedEmail.id === emailId) {
        console.log('🔄 Reloading selected email details...');
        const updatedEmail = await emailService.getEmail(emailId);
        setSelectedEmail(updatedEmail);
        console.log('📧 Updated email:', updatedEmail);
      }
      
      showNotification('Email updated successfully', 'success');
      console.log('✅ EmailManagement update completed');
    } catch (error) {
      console.error('❌ Error updating email:', error);
      showNotification('Error updating email', 'error');
    }
    
    console.groupEnd();
  };

  const handleFolderSelect = (folderName: string) => {
    setSelectedFolder(folderName);
    setSelectedEmail(null);
  };

  const handleComposeComplete = () => {
    setComposeOpen(false);
    setComposeMode('compose');
    setReplyToEmail(null);
    loadEmails();
    showNotification('Email sent successfully', 'success');
  };

  const handleReply = (emailId: string) => {
    const email = emails.find(e => e.id === emailId) || selectedEmail;
    if (email) {
      setReplyToEmail(email);
      setComposeMode('reply');
      setComposeOpen(true);
    }
  };

  const handleForward = (emailId: string) => {
    const email = emails.find(e => e.id === emailId) || selectedEmail;
    if (email) {
      setReplyToEmail(email);
      setComposeMode('forward');
      setComposeOpen(true);
    }
  };

  const handleCompose = () => {
    setComposeMode('compose');
    setReplyToEmail(null);
    setComposeOpen(true);
  };

  const handleFiltersApply = (newFilters: any) => {
    setFilters(newFilters);
    setFiltersOpen(false);
  };

  const getFolderIcon = (folderName: string) => {
    const iconMap: Record<string, React.ReactElement> = {
      inbox: <InboxIcon />,
      sent: <SendIcon />,
      drafts: <DraftIcon />,
      trash: <DeleteIcon />,
      archive: <ArchiveIcon />
    };
    return iconMap[folderName] || <FolderIcon />;
  };

  const getTotalUnreadCount = () => {
    return folders.system.find(f => f.name === 'inbox')?.count || 0;
  };

  return (
    <StyledContainer>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
          📧 Email Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Unified email communication system
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Sidebar */}
        <Grid item xs={12} md={3}>
          <SidebarPaper>
            {/* Search */}
            <SearchTextField
              fullWidth
              placeholder="Search emails..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleSearch}>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ mb: 2 }}
            />

            {/* Action Buttons */}
            <Box sx={{ mb: 2 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCompose}
                sx={{ mb: 1 }}
              >
                Compose
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => setFiltersOpen(true)}
                sx={{ mb: 1 }}
              >
                Filters
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<BusinessIcon />}
                onClick={() => setSupplierDialogOpen(true)}
              >
                Suppliers
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Folders */}
            <Typography variant="subtitle2" gutterBottom>
              Folders
            </Typography>
            <List dense>
              {folders.system.map((folder) => (
                <FolderListItem
                  key={folder.name}
                  selected={selectedFolder === folder.name}
                  onClick={() => handleFolderSelect(folder.name)}
                >
                  <ListItemIcon>
                    {getFolderIcon(folder.name)}
                  </ListItemIcon>
                  <ListItemText 
                    primary={folder.name}
                    sx={{ textTransform: 'capitalize' }}
                  />
                  {folder.count > 0 && (
                    <Badge badgeContent={folder.count} color="primary" />
                  )}
                </FolderListItem>
              ))}
            </List>

            {/* Custom Folders */}
            {folders.custom.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Custom Folders
                </Typography>
                <List dense>
                  {folders.custom.map((folder) => (
                    <FolderListItem
                      key={folder.name}
                      selected={selectedFolder === folder.name}
                      onClick={() => handleFolderSelect(folder.name)}
                    >
                      <ListItemIcon>
                        <FolderIcon />
                      </ListItemIcon>
                      <ListItemText primary={folder.name} />
                      {folder.count > 0 && (
                        <Badge badgeContent={folder.count} color="primary" />
                      )}
                    </FolderListItem>
                  ))}
                </List>
              </>
            )}

            {/* Labels */}
            {labels.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Labels
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {labels.map((label) => (
                    <Chip
                      key={label.name}
                      label={label.name}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: label.color,
                        color: label.color,
                        '&:hover': {
                          backgroundColor: `${label.color}20`,
                        }
                      }}
                    />
                  ))}
                </Box>
              </>
            )}
          </SidebarPaper>
        </Grid>

        {/* Email List */}
        <Grid item xs={12} md={9}>
          <StyledPaper>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                {selectedFolder.charAt(0).toUpperCase() + selectedFolder.slice(1)} 
                {emails.length > 0 && ` (${emails.length})`}
              </Typography>
              <IconButton onClick={loadEmails} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : emails.length === 0 ? (
              <Alert severity="info">
                No emails found in {selectedFolder}
              </Alert>
            ) : (
              <EmailList
                emails={emails}
                selectedEmail={selectedEmail}
                onEmailSelect={handleEmailSelect}
                onEmailUpdate={handleEmailUpdate}
              />
            )}
          </StyledPaper>
        </Grid>

        {/* Email Detail */}
        {selectedEmail && (
          <Dialog
            open={!!selectedEmail}
            onClose={() => setSelectedEmail(null)}
            maxWidth="lg"
            fullWidth
            PaperProps={{
              sx: {
                height: '80vh',
                maxHeight: '800px'
              }
            }}
          >
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6" component="div">
                  {selectedEmail.subject}
                </Typography>
                <IconButton onClick={() => setSelectedEmail(null)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <EmailDetail
                email={selectedEmail}
                onEmailUpdate={handleEmailUpdate}
                onClose={() => setSelectedEmail(null)}
                onReply={handleReply}
                onForward={handleForward}
              />
            </DialogContent>
          </Dialog>
        )}
      </Grid>

      {/* Floating Action Button */}
      <StyledFab
        color="primary"
        aria-label="compose"
        onClick={handleCompose}
      >
        <AddIcon />
      </StyledFab>

      {/* Compose Email Dialog */}
      <EmailCompose
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        onSent={handleComposeComplete}
        replyToEmail={replyToEmail || undefined}
        mode={composeMode}
      />

      {/* Filters Dialog */}
      <EmailFilters
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        onApply={handleFiltersApply}
        currentFilters={filters}
        labels={labels}
      />

      {/* Supplier Management Dialog */}
      <SupplierManagementDialog
        open={supplierDialogOpen}
        onClose={() => setSupplierDialogOpen(false)}
        emailContext={selectedEmail ? {
          email_id: selectedEmail.id,
          from_address: selectedEmail.from_address,
          subject: selectedEmail.subject,
          body_text: selectedEmail.body_text
        } : {
          email_id: '',
          from_address: '',
          subject: '',
          body_text: ''
        }}
      />
    </StyledContainer>
  );
};

export default EmailManagement;
